import React from "react";
import ReactDOM from "react-dom/client";
import { initBrands } from "./configs/brandsSource";
import {
  findBrandByDomain,
  setInjectedConfig,
} from "./configs/configLoader";
import { initObservability, recordException } from "./utils/observability";
import { initAnalytics } from "./utils/analytics";
import { checkBuildFreshness } from "./utils/versionCheck";

import "./index.css";

function getEnvironment(): string {
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return "local";
  if (host.includes("dev")) return "development";
  if (host.includes("staging")) return "staging";
  return "production";
}

class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    recordException(error, {
      "react.componentStack": errorInfo.componentStack ?? "",
    });
  }

  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

// Reload stale long-lived instances onto the latest deployed bundle.
checkBuildFreshness();

// La liste des marques (brands.json du CDN, fallback brandsArray local) doit
// être résolue AVANT d'importer App : configs/index.ts, stylesLoader.ts et
// i18n.ts évaluent getConfig()/findBrandByDomain() à l'import. D'où les
// imports dynamiques ci-dessous — tout ce qui dépend de la config attend
// initBrands(), qui ne rejette jamais (fallback local au pire).
async function boot(): Promise<void> {
  await initBrands();

  const urlParams = new URLSearchParams(window.location.search);
  const environment = getEnvironment();
  const domain = urlParams.get("domain") ?? window.location.hostname;
  const brandName = findBrandByDomain()?.name ?? "unknown";
  const productId =
    urlParams.get("pid") ??
    urlParams.get("productId") ??
    urlParams.get("product_id") ??
    "unknown";
  const lang = urlParams.get("lang") ?? "unknown";
  const market =
    urlParams.get("market") ??
    urlParams.get("country_code") ??
    urlParams.get("countryCode") ??
    "unknown";
  const category = urlParams.get("category") ?? "unknown";

  const apiKey = process.env.REACT_APP_HYPERDX_API_KEY;
  const hyperdxUrl = process.env.REACT_APP_HYPERDX_URL;
  if (apiKey) {
    initObservability({
      apiKey,
      service: "drawer",
      url: hyperdxUrl,
      globalAttributes: {
        environment,
        domain,
        brand: brandName,
        pid: productId,
        lang,
        market,
        category,
      },
    });
  }

  const posthogKey = process.env.REACT_APP_POSTHOG_KEY;
  if (posthogKey) {
    initAnalytics({
      apiKey: posthogKey,
      apiHost: process.env.REACT_APP_POSTHOG_HOST,
      debug: environment === "local",
      globalProperties: {
        environment,
        domain,
        brand: brandName,
        pid: productId,
        lang,
        market,
        category,
      },
    });
    // Note: do NOT fire drawer_opened here. The iframe mounts on every PDP load
    // even when the user never clicks the kleep widget. drawer_opened is fired
    // from DrawerModal on the first real interaction (first step transition).
  }

  const root = ReactDOM.createRoot(
    document.getElementById("root") as HTMLElement,
  );

  // App import is dynamic AND deferred: importing App evaluates getConfig()
  // (configs/index.ts, stylesLoader, i18n). In dashboard mode we must wait for
  // the injected config before that evaluation, hence the import lives here.
  async function renderApp() {
    const [{ default: App }, { UserContextProvider }] = await Promise.all([
      import("./App"),
      import("./store/userContext"),
    ]);

    root.render(
      <ErrorBoundary>
        <UserContextProvider>
          <App />
        </UserContextProvider>
      </ErrorBoundary>,
    );
  }

  const isDashboard = urlParams.get("mode") === "dashboard";

  if (isDashboard) {
    window.addEventListener("message", (event) => {
      if (event.data?.action === "inject_config") {
        setInjectedConfig(event.data.config);
        renderApp();
      }
    });
    window.parent.postMessage({ action: "drawer_ready" }, "*");
  } else {
    renderApp();
  }
}

boot();
